import { listInputFrom, ordersSearchSchema } from "@commerce/orders/meta";
import { OrdersScreen, OrdersSkeleton } from "@commerce/orders/OrdersScreen";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { orpc } from "@shared/lib/orpc";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/orders/",
)({
	validateSearch: ordersSearchSchema,
	staticData: { fullBleed: true },
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
	}),
	// Prefetch the view the URL asks for once; queue and filter changes are client queries.
	shouldReload: false,
	loader: async ({ context, location }) => {
		const organizationId = context.organization.id;
		const search = ordersSearchSchema.parse(location.search);
		const queryClient = getServerQueryClient();
		// A failed prefetch is not the page failing: the screen's own query retries it and
		// its boundary shows what went wrong.
		await Promise.all([
			queryClient
				.ensureQueryData(
					orpc.orders.list.queryOptions({
						input: listInputFrom(organizationId, search),
					}),
				)
				.catch(() => null),
			queryClient
				.ensureQueryData(
					orpc.orders.counts.queryOptions({
						input: {
							organizationId,
							...(search.store ? { storeId: search.store } : {}),
						},
					}),
				)
				.catch(() => null),
			search.id
				? queryClient
						.ensureQueryData(
							orpc.orders.get.queryOptions({
								input: { organizationId, orderId: search.id },
							}),
						)
						.catch(() => null)
				: null,
		]);
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	// Shown inside the Shell while the loader runs on a client navigation.
	pendingComponent: OrdersSkeleton,
	head: () => ({ meta: [{ title: `Orders - ${config.appName}` }] }),
	component: OrdersPage,
});

function OrdersPage() {
	const { organizationId, dehydratedState } = Route.useLoaderData();
	const { isOrganizationAdmin } = Route.useRouteContext();
	return (
		<AsyncBoundary
			fallback={<OrdersSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<OrdersScreen
				organizationId={organizationId}
				isAdmin={isOrganizationAdmin}
			/>
		</AsyncBoundary>
	);
}
